import { ChevronRight, MarkerPin01 } from '@untitledui/icons'
import { usePress } from 'react-aria'

import { RadioButton, RadioButtonBase } from '@components/designSystem/Radio'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { type Store } from '@state/store'

function StoreInfo({ store }: { store: Store }) {
  const { t } = useTypedTranslation()
  const address = store.address
  return (
    <>
      <MarkerPin01 className="size-6 text-gray-900 group-hover:text-text-brand-primary" />

      <div className="col-start-2">
        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-text-brand-primary">{store.name}</h3>
      </div>

      <div className="col-start-2 text-sm font-medium text-gray-600">
        {
          address ?
            <>
              <p>{address.addressLines[0]}</p>
              <p>{`${address.postalCode} ${address.city}`}</p>
              <p>{address.regionName}, {address.countryName}</p>
            </>
            :
            <p className="text-sm text-gray-500 italic">{t('storeSelection.selectStore.noAddress')}</p>
        }
      </div>
    </>
  )
}

interface ButtonProps {
  store: Store
  onClick: () => void
}

function StoreCardButton({ store, onClick }: ButtonProps) {

  const { pressProps } = usePress({ onPress: onClick })

  return (
    <button
      type="button"
      className={`
        group flex w-full cursor-pointer flex-col gap-3 rounded-lg border border-transparent bg-white p-5 text-left
        shadow-xs-skeumorphic transition-all
        hover:border-border-brand
      `}
      {...pressProps}
    >
      <div data-testid='store-card' className={`
        grid w-full grid-cols-[auto_1fr_auto] grid-rows-[auto_auto] items-center gap-x-3 gap-y-1
      `}>
        <StoreInfo store={store} />
        <ChevronRight className='col-start-3 row-span-full items-center group-hover:text-brand-primary' />
      </div>
    </button>
  )
}

interface RadioProps {
  store: Store
}

function StoreCardRadio({ store }: RadioProps) {
  return (
    <RadioButton
      value={store.id}
      className={`
        group flex cursor-pointer flex-col gap-3 rounded-lg bg-white p-5 shadow-xs-skeumorphic transition-all
        hover:border-border-brand
      `}
    >
      {(radioProps) => (
        <div data-testid='store-card' className={`
          grid w-full grid-cols-[auto_1fr_auto] grid-rows-[auto_auto] items-center gap-x-3
        `}>
          <StoreInfo store={store} />
          <RadioButtonBase className='col-start-3 row-span-full items-center' size='md' {...radioProps} />
        </div>
      )}
    </RadioButton>
  )
}

function StoreCardSkeleton() {
  return (
    <div className="flex animate-pulse flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="grid grid-cols-[auto_1fr_auto] grid-rows-[auto_auto] items-center gap-x-3">
        {/* 1. Icon Placeholder */}
        <div className="size-5 rounded-full bg-gray-200" />

        {/* 2. Store Name Placeholder */}
        <div className="col-start-2 h-6 w-3/4 rounded-sm bg-gray-200" />

        {/* 3. Button Placeholder */}
        <div className="h-9 w-20 rounded-sm bg-gray-200" />

        {/* 4. Address Block Placeholder */}
        <div className="col-start-2 mt-1 space-y-1.5">
          <div className="h-4 w-2/3 rounded-sm bg-gray-200" />
          <div className="h-4 w-1/2 rounded-sm bg-gray-200" />
          <div className="h-4 w-2/5 rounded-sm bg-gray-200" />
        </div>
      </div>
    </div>
  )
}

const StoreCard = {
  Button: StoreCardButton,
  Radio: StoreCardRadio,
  Skeleton: StoreCardSkeleton,
}

export default StoreCard

